/**
 * TranscriptRecorder — captures audio from a MediaStream, slices it into
 * ~25-second WebM/Opus chunks, and POSTs each one to the backend Whisper
 * transcription endpoint as it's produced.
 *
 * Why chunks (not one big upload)?
 * - The broker can review the transcript live as the meeting unfolds.
 * - Whisper's 25 MB cap is a hard ceiling; chunks stay well under it.
 * - If the network blips during a 30-min call, we don't lose the entire
 *   recording — only the in-flight chunk.
 *
 * Public API:
 *   const r = new TranscriptRecorder({ stream, meetingId, token, onSegment });
 *   r.start();   // begins capture + auto-uploads
 *   r.stop();    // flushes the final chunk and stops
 */
const CHUNK_DURATION_MS = 25_000; // ~25s per chunk

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return '';
  }
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4', // Safari (iOS 14.5+)
    'audio/mp4;codecs=mp4a.40.2',
  ];
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
};

export class TranscriptRecorder {
  /**
   * @param {Object} opts
   * @param {MediaStream} opts.stream     - The local stream (mic) to record
   * @param {string} opts.meetingId       - Meeting UUID
   * @param {string} opts.token           - Either host_token or guest_token (the same one used for WS)
   * @param {string} [opts.speakerName]   - Display name for the speaker
   * @param {(segment: object) => void} [opts.onSegment]  - Fired after each chunk uploads
   * @param {(err: Error) => void}        [opts.onError]
   * @param {string} opts.backendUrl      - REACT_APP_BACKEND_URL
   */
  constructor({ stream, meetingId, token, speakerName, onSegment, onError, backendUrl }) {
    this.stream = stream;
    this.meetingId = meetingId;
    this.token = token;
    this.speakerName = speakerName || null;
    this.onSegment = onSegment || (() => {});
    this.onError = onError || ((e) => console.warn('TranscriptRecorder', e));
    this.backendUrl = backendUrl;
    this.recorder = null;
    this.chunks = [];
    this.chunkStartedAt = null;
    this.timer = null;
    this.recording = false;
    this.mimeType = pickMimeType();
  }

  /** True iff the browser supports recording. */
  static isSupported() {
    return typeof MediaRecorder !== 'undefined' && !!pickMimeType();
  }

  start() {
    if (this.recording) return;
    if (!TranscriptRecorder.isSupported()) {
      this.onError(new Error('MediaRecorder not supported in this browser'));
      return;
    }
    if (!this.stream || this.stream.getAudioTracks().length === 0) {
      this.onError(new Error('No audio track to record'));
      return;
    }
    // Record only audio — the video tracks add bandwidth without helping
    // Whisper. Build a mic-only MediaStream from the source.
    const audioOnly = new MediaStream(this.stream.getAudioTracks());

    this._cycle = () => {
      try {
        this._stopAndFlushCurrent();
        this._startNewSegment(audioOnly);
      } catch (e) {
        this.onError(e);
      }
    };
    this._startNewSegment(audioOnly);
    this.timer = setInterval(this._cycle, CHUNK_DURATION_MS);
    this.recording = true;
  }

  stop() {
    if (!this.recording) return;
    this.recording = false;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this._stopAndFlushCurrent();
  }

  _startNewSegment(audioStream) {
    this.chunks = [];
    this.chunkStartedAt = new Date().toISOString();
    const opts = this.mimeType ? { mimeType: this.mimeType } : undefined;
    try {
      this.recorder = new MediaRecorder(audioStream, opts);
    } catch (e) {
      // Some browsers throw on unsupported mimeType — retry without
      this.recorder = new MediaRecorder(audioStream);
    }
    this.recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) this.chunks.push(event.data);
    };
    this.recorder.onstop = () => {
      const blob = new Blob(this.chunks, { type: this.mimeType || 'audio/webm' });
      const startedAt = this.chunkStartedAt;
      const duration = (Date.now() - new Date(startedAt).getTime()) / 1000;
      // Only upload chunks with substance — pure silence < ~2s tends to be
      // hot-mic noise that Whisper transcribes as "you" or "thanks"
      if (blob.size < 8_000) return;
      this._uploadChunk(blob, startedAt, duration);
    };
    this.recorder.start();
  }

  _stopAndFlushCurrent() {
    if (this.recorder && this.recorder.state !== 'inactive') {
      try { this.recorder.stop(); } catch (e) { /* ignore */ }
    }
  }

  async _uploadChunk(blob, startedAt, durationSecs) {
    try {
      const fd = new FormData();
      const ext = (this.mimeType.includes('mp4') ? 'mp4' : 'webm');
      fd.append('audio', blob, `chunk-${Date.now()}.${ext}`);
      fd.append('token', this.token);
      fd.append('started_at', startedAt);
      fd.append('duration_secs', String(durationSecs));
      if (this.speakerName) fd.append('speaker_name', this.speakerName);
      const res = await fetch(`${this.backendUrl}/api/meetings/${this.meetingId}/transcript/chunk`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) throw new Error(`Upload failed ${res.status}`);
      const segment = await res.json();
      if (segment && segment.text) this.onSegment(segment);
    } catch (e) {
      this.onError(e);
    }
  }
}

export default TranscriptRecorder;
